import { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { api, TOKEN_KEY } from "@/lib/api";
import { T } from "@/lib/testIds";

export function useAdminAuth() {
  const [state, setState] = useState({ ready: false, ok: false });
  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) { setState({ ready: true, ok: false }); return; }
    api.get("/admin/me").then(() => setState({ ready: true, ok: true }))
      .catch(() => setState({ ready: true, ok: false }));
  }, []);
  return state;
}

export function AdminGuard({ children }) {
  const { ready, ok } = useAdminAuth();
  const loc = useLocation();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)]">
        <p className="text-[10px] tracking-[0.24em] uppercase text-[var(--ink-soft)]">Verifying…</p>
      </div>
    );
  }
  if (!ok) return <Navigate to="/admin/login" state={{ from: loc.pathname }} replace />;
  return children;
}

export default function AdminLogin() {
  const [passphrase, setPassphrase] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) {
      api.get("/admin/me").then(() => navigate("/admin", { replace: true })).catch(() => {});
    }
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { passphrase });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      const to = loc.state?.from || "/admin";
      navigate(to, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] grid lg:grid-cols-2">
      {/* Left editorial panel */}
      <div className="relative hidden lg:block overflow-hidden bg-[var(--ink)]">
        <img
          alt="Studio"
          src="https://images.unsplash.com/photo-1487530811176-3780de880c2d?auto=format&fit=crop&w=1400&q=80"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[rgba(15,20,17,0.55)] to-transparent" />
        <div className="relative h-full p-14 flex flex-col justify-between text-[var(--bg)]">
          <div>
            <p className="text-[10px] tracking-[0.32em] uppercase opacity-80 mb-3">Studio · Admin</p>
            <p className="font-serif text-5xl leading-[0.95]">
              A weekly ritual,<br/><em className="italic font-light">softly staged.</em>
            </p>
          </div>
          <div className="text-[10px] tracking-[0.24em] uppercase opacity-70">© BUDS Floral Club · Bangalore</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 sm:p-14">
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
        >
          <a href="/" className="btn-ghost mb-16 inline-flex"><ArrowLeft size={14}/> Back to site</a>
          <p className="chapter-num text-5xl mb-2">§</p>
          <h1 className="font-serif text-5xl leading-[0.95] mb-4">Enter the studio.</h1>
          <p className="text-[var(--ink-soft)] leading-relaxed mb-12">
            The customer roster, delivery calendar and payment queue are for the BUDS studio only. Please enter your team passphrase.
          </p>

          <form onSubmit={submit} className="space-y-10">
            <label className="block">
              <span className="block text-[10px] tracking-[0.24em] uppercase text-[var(--sage-deep)] mb-2">Team passphrase</span>
              <input
                data-testid={T.admin.loginPassphrase}
                type="password"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                className="editorial-input"
                autoFocus
                placeholder="••••••••"
              />
            </label>

            {error && (
              <motion.p
                data-testid={T.admin.loginError}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="text-xs uppercase tracking-[0.22em] text-[var(--ink)] border-l-2 border-[var(--ink)] pl-4"
              >
                {error}
              </motion.p>
            )}

            <button data-testid={T.admin.loginSubmit} type="submit" disabled={loading || !passphrase}
              className="btn-primary w-full justify-between">
              <span>{loading ? "Unlocking…" : "Unlock studio"}</span>
              <ArrowRight size={14} />
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
